import React, { useMemo, useRef, useLayoutEffect, useCallback, useState, useEffect } from "react";
import "./Providers.css";

import boclips from "../../assets/boclips.png";
import highlights from "../../assets/highlights.png";
import samlearning from "../../assets/samlearning.png";
import pickatale from "../../assets/pickatale.png";
import everybodycounts from "../../assets/everybodycounts.png";
import bookr from '../../assets/bookr.png';

const BASE_LOGOS = [
  { src: boclips, alt: "Boclips" },
  { src: highlights, alt: "Highlights" },
  { src: samlearning, alt: "SAM Learning" },
  { src: pickatale, alt: "Pickatale" },
  { src: everybodycounts, alt: "Everybody Counts" },
  { src: bookr, alt: "Bookr" },
];

// Optimized hook for mobile performance
function useRepeatedLogos() {
  return useMemo(() => {
    const OUT = [];
    // Reduce tiles on mobile for better performance
    const isMobile = window.innerWidth <= 768;
    const MIN_TILES = isMobile ? 20 : 40; // Half the tiles on mobile
    const times = Math.ceil(MIN_TILES / BASE_LOGOS.length);
    for (let t = 0; t < times; t++) OUT.push(...BASE_LOGOS);
    return OUT;
  }, []);
}

// Hook to detect reduced motion preference
function useReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  return prefersReducedMotion;
}

export default function Providers() {
  const REPEATED = useRepeatedLogos();
  const prefersReducedMotion = useReducedMotion();
  const [isVisible, setIsVisible] = useState(false);

  // NEW: keep speed constant (px/sec) no matter the viewport
  const marqueeHostRef = useRef(null);   // .providers-marquee
  const stripARef = useRef(null);        // first .marquee-strip (one loop length)

  // Optimized update function with throttling
  const update = useCallback(() => {
    const strip = stripARef.current;
    const host = marqueeHostRef.current;
    if (!strip || !host) return;
    
    const oneLoopPx = strip.scrollWidth; // width of one repeated strip
    const isMobile = window.innerWidth <= 768;
    const PX_PER_SECOND = isMobile ? 30 : 60; // Slower on mobile for better performance
    const seconds = oneLoopPx / PX_PER_SECOND;
    host.style.setProperty("--providers-duration", `${seconds}s`);
  }, []);

  // Throttled resize handler
  const throttledUpdate = useCallback(() => {
    let timeoutId;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(update, 100);
    };
  }, [update])();

  useLayoutEffect(() => {
    update();

    const ro = new ResizeObserver(update);
    if (stripARef.current) ro.observe(stripARef.current);
    if (marqueeHostRef.current) ro.observe(marqueeHostRef.current);
    window.addEventListener("resize", throttledUpdate);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", throttledUpdate);
    };
  }, [update, throttledUpdate]);

  // Intersection Observer for performance optimization
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (marqueeHostRef.current) {
      observer.observe(marqueeHostRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="providers" className="providers">
      <div className="providers-inner">
        <header className="providers-header">
          <h2 className="providers-title">Trusted Platforms</h2>
          <p className="providers-subtitle">
            We partner with leading education brands to deliver engaging, high-quality learning content for all ages.
          </p>
        </header>

        <div 
          className={`providers-marquee ${prefersReducedMotion ? 'reduced-motion' : ''} ${!isVisible ? 'paused' : ''}`} 
          aria-label="Partner logos carousel" 
          ref={marqueeHostRef}
        >
          <div className="marquee-inner" role="list">
            {/* Strip A */}
            <div className="marquee-strip" aria-hidden="false" ref={stripARef}>
              {REPEATED.map((logo, i) => (
                <div className="logo-tile" role="listitem" key={`A-${i}`}>
                  <img 
                    src={logo.src} 
                    alt={logo.alt} 
                    loading={isVisible ? "lazy" : "eager"}
                    decoding="async"
                    style={{ willChange: isVisible ? 'transform' : 'auto' }}
                  />
                </div>
              ))}
            </div>
            {/* Strip B (identical copy) */}
            <div className="marquee-strip" aria-hidden="true">
              {REPEATED.map((logo, i) => (
                <div className="logo-tile" role="listitem" key={`B-${i}`}>
                  <img 
                    src={logo.src} 
                    alt="" 
                    loading={isVisible ? "lazy" : "eager"}
                    decoding="async"
                    style={{ willChange: isVisible ? 'transform' : 'auto' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
